import React, { useCallback } from 'react';
import { TransferHistoryRecord, GameState } from '../../../domain/entities';
import { View } from '../../../App';
import { TeamBadge } from '../../shared/TeamBadge';

interface PlayerTransferHistoryPanelProps {
    transferHistory: TransferHistoryRecord[];
    gameState: GameState;
    pushView: (view: View) => void;
}

export const PlayerTransferHistoryPanel: React.FC<PlayerTransferHistoryPanelProps> = ({ transferHistory, gameState, pushView }) => {

    const getTeamLeague = useCallback((teamName: string): string | null => {
        for (const leagueName in gameState.leagues) {
            if (gameState.leagues[leagueName].teams.some(t => t.name === teamName)) {
                return leagueName;
            }
        }
        return null;
    }, [gameState.leagues]);

    const getTeamByName = useCallback((name: string) => {
        if (name === 'Youth Academy') return undefined;
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    }, [gameState.leagues]);

    const handleTeamClick = (teamName: string) => {
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName: teamName });
    };

    return (
        <div className="panel">
            <div className="panel-header">Transfer History</div>
            <div className="panel-content" style={{ padding: 0 }}>
                {transferHistory.length > 0 ? (
                    <div className="table-wrapper">
                        <table className="table player-history-table">
                            <thead>
                                <tr>
                                    <th>S/W</th>
                                    <th>From</th>
                                    <th>To</th>
                                    <th style={{ textAlign: 'right' }}>Fee</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transferHistory.map((record, index) => {
                                    const fromLeague = getTeamLeague(record.fromTeam);
                                    const toLeague = getTeamLeague(record.toTeam);

                                    const fromText = record.fromTeam === 'Youth Academy'
                                        ? 'Youth Academy'
                                        : (<button className="link-style" onClick={() => handleTeamClick(record.fromTeam)}>{getTeamByName(record.fromTeam)?.badge && <TeamBadge badge={getTeamByName(record.fromTeam)?.badge} color={getTeamByName(record.fromTeam)?.colors.primary} size="sm" />}{`${record.fromTeam} ${fromLeague ? `(${fromLeague})` : ''}`}</button>);

                                    const toText = (<button className="link-style" onClick={() => handleTeamClick(record.toTeam)}>{getTeamByName(record.toTeam)?.badge && <TeamBadge badge={getTeamByName(record.toTeam)?.badge} color={getTeamByName(record.toTeam)?.colors.primary} size="sm" />}{`${record.toTeam} ${toLeague ? `(${toLeague})` : ''}`}</button>);

                                    const feeText = record.fee === 0 && record.fromTeam === 'Youth Academy'
                                        ? 'Youth'
                                        : `£${record.fee.toLocaleString()}`;

                                    return (
                                        <tr key={index}>
                                            <td style={{ whiteSpace: 'nowrap' }}>S{record.season} W{Math.floor(record.week)}</td>
                                            <td>{fromText}</td>
                                            <td>{toText}</td>
                                            <td style={{ textAlign: 'right' }}>{feeText}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-secondary" style={{ padding: '12px', textAlign: 'center' }}>This player has only been at this club.</p>
                )}
            </div>
        </div>
    );
};
